import { FC } from 'react'
import { Locales } from '@carsys/enums/locales'
import { Box, InputAdornment } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Form, TextField } from '@carsys/form'
import { useVATFieldValidation } from '@cbgms/base/utils/validations'
import { useVATValidatorBuilder } from 'app/auth/components/hooks/use-vat-validator-builder'
import * as Yup from 'yup'
import { BaseStepLayout } from '../BaseStepLayout'
import { StepNavigation } from '../StepNavigation'
import { defaultStepFormProps } from '../types.d'
import HelpIcon from '@mui/icons-material/Help'
import Tooltip from '@mui/material/Tooltip'
import { WithRunInBackgroundHelper } from '../use-should-run-in-background'

export const VatNumberForm: FC<defaultStepFormProps & { onSubmit: (vatNumber: string) => void; basePath: string; country?: string }> = ({
  onSubmit,
  basePath,
  country,
  prevStep,
  isFirstStep,
  isPersisting,
  onSubmitSuccess,
  hideSkip,
  shouldRunInBackground,
  initialData,
  duplicationCheck
}) => {
  const { t } = useTranslation()
  const vatValidation = useVATFieldValidation(country)
  const submitHandler = useVATValidatorBuilder(duplicationCheck, basePath)

  const setKeyVATTooltip = country ? `setting_my_shop.vattooltip_${country}` : 'setting_my_shop.vattooltip'
  const validationSchema = Yup.object().shape({
    VatNumber: Yup.string().when({
      is: (value: string) => value && value.length > 0,
      then: schema =>
        schema
          .test(
            'VatNumber',
            ({ value }) => vatValidation.validator(value),
            value => !vatValidation.validator(value)
          )
          .test(async value => {
            if (value) {
              await submitHandler.mutateAsync({ VATNumber: value })
              return true
            }
            return false
          }),
      otherwise: schema => schema.optional()
    })
  })

  return (
    <BaseStepLayout title={t('Auth.OnboardingTitle')} subTitles={[t('Auth.OnboardingVatNumberMessage')]}>
      <Form
        enableReinitialize
        NavigationConfirmation={null}
        validationSchema={validationSchema}
        initialValues={{
          Locale: Locales.en_GB,
          VatNumber: '',
          ...initialData
        }}
        onSubmit={async v => {
          onSubmit(v.VatNumber)
          return v
        }}
        onSubmitSuccess={onSubmitSuccess}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({ isSubmitting, handleSubmit }) => (
          <WithRunInBackgroundHelper enabled={shouldRunInBackground} isSubmitting={isSubmitting} onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', '& > *': { mb: 1 } }}>
              <Box>
                <TextField
                  name='VatNumber'
                  label={t('setting_my_shop.vatidentification')}
                  autoFocus
                  InputProps={{
                    inputProps: { maxLength: vatValidation.maxLength },
                    endAdornment: (
                      <InputAdornment position='end'>
                        <Tooltip title={`${t(setKeyVATTooltip)}`}>
                          <HelpIcon />
                        </Tooltip>
                      </InputAdornment>
                    )
                  }}
                  fullWidth={false}
                />
              </Box>
            </Box>

            <StepNavigation
              prevStep={prevStep}
              isLoading={submitHandler.isLoading || isPersisting}
              hideBack={isFirstStep}
              hideSkip={hideSkip}
            />
          </WithRunInBackgroundHelper>
        )}
      </Form>
    </BaseStepLayout>
  )
}
